import React, {useCallback, useEffect, useState} from 'react';
import {Loader2, RefreshCw} from 'lucide-react';
import {toast} from 'react-toastify';
import AdminBioMatchFulfillmentService, {
    type AdminFulfillmentRow,
} from '../services/adminBioMatchFulfillmentService';
import AdminAddBloodSampleModal from '../components/AdminAddBloodSampleModal';
import {logError} from '@/shared/utils/logger';
import {dollars} from '@/shared/utils/money';

/**
 * Admin "Pending BioMatch Fulfillment" queue. One row per enrollment
 * that needs ops attention — either a kit to ship (home_kit) or a
 * lab add-on to run against an existing draw.
 *
 * Until proper scheduler infra ships, the "Run cycle scan" button
 * lets ops manually trigger the recurring 90-day requeue.
 */

function methodLabel(m: string): string {
    return m === 'add_to_existing_draw' ? 'Lab add-on' : 'Home kit';
}

const AdminBioMatchFulfillmentView: React.FC = () => {
    const [rows, setRows] = useState<AdminFulfillmentRow[]>([]);
    const [loading, setLoading] = useState(false);
    const [acting, setActing] = useState<number | null>(null);
    const [scanning, setScanning] = useState(false);
    const [bloodModalOpen, setBloodModalOpen] = useState(false);

    const refresh = useCallback(async () => {
        setLoading(true);
        try {
            const data = await AdminBioMatchFulfillmentService.list();
            setRows(data);
        } catch (err) {
            logError('Failed to load fulfillment queue', err);
            toast.error('Could not load the fulfillment queue.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        refresh();
    }, [refresh]);

    const onMark = async (row: AdminFulfillmentRow) => {
        setActing(row.enrollment_id);
        try {
            const action = row.fulfillment_method === 'add_to_existing_draw' ? 'lab_addon_complete' : 'ship';
            await AdminBioMatchFulfillmentService.mark(row.enrollment_id, {action});
            toast.success(action === 'ship' ? 'Marked as shipped' : 'Lab add-on marked complete');
            await refresh();
        } catch (err) {
            logError('Mark fulfillment failed', err);
            toast.error('Could not mark this enrollment.');
        } finally {
            setActing(null);
        }
    };

    const onScan = async () => {
        setScanning(true);
        try {
            const {requeued} = await AdminBioMatchFulfillmentService.runRecurringScan();
            toast.success(`Recurring scan complete — ${requeued} enrollment${requeued === 1 ? '' : 's'} requeued.`);
            await refresh();
        } catch (err) {
            logError('Cycle scan failed', err);
            toast.error('Recurring scan failed.');
        } finally {
            setScanning(false);
        }
    };

    return (
        <div className="space-y-6 p-6">
            <AdminAddBloodSampleModal
                open={bloodModalOpen}
                onClose={() => setBloodModalOpen(false)}
            />
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                    <h1 className="text-2xl font-semibold text-mld-text-primary">BioMatch Fulfillment</h1>
                    <p className="text-sm text-mld-text-secondary">
                        Pending kit shipments and lab add-ons. Mark items as completed once they're processed.
                    </p>
                </div>
                <div className="flex flex-wrap gap-2">
                    <button
                        type="button"
                        onClick={() => setBloodModalOpen(true)}
                        className="inline-flex items-center gap-1.5 rounded-md border border-mld-panel-border px-3 py-1.5 text-sm hover:bg-mld-panel-bg">
                        Add blood sample
                    </button>
                    <button
                        type="button"
                        onClick={refresh}
                        disabled={loading}
                        className="inline-flex items-center gap-1.5 rounded-md border border-mld-panel-border px-3 py-1.5 text-sm hover:bg-mld-panel-bg disabled:opacity-50">
                        <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
                        Refresh
                    </button>
                    <button
                        type="button"
                        onClick={onScan}
                        disabled={scanning}
                        className="inline-flex items-center gap-1.5 rounded-md bg-mld-primary px-3 py-1.5 text-sm font-medium text-white hover:opacity-90 disabled:opacity-60">
                        {scanning && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                        Run 90-day cycle scan
                    </button>
                </div>
            </div>

            <div className="overflow-x-auto rounded-lg border border-mld-panel-border bg-white">
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                    <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
                        <tr>
                            <th className="px-4 py-3 text-left">Patient</th>
                            <th className="px-4 py-3 text-left">Organization</th>
                            <th className="px-4 py-3 text-left">Program</th>
                            <th className="px-4 py-3 text-left">Method / SKU</th>
                            <th className="px-4 py-3 text-left">Address</th>
                            <th className="px-4 py-3 text-left">Cost split</th>
                            <th className="px-4 py-3 text-left">Enrolled</th>
                            <th className="px-4 py-3" />
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                        {loading && rows.length === 0 ? (
                            <tr>
                                <td colSpan={8} className="px-4 py-10 text-center text-mld-text-secondary">
                                    Loading…
                                </td>
                            </tr>
                        ) : rows.length === 0 ? (
                            <tr>
                                <td colSpan={8} className="px-4 py-10 text-center text-mld-text-secondary">
                                    No pending fulfillments.
                                </td>
                            </tr>
                        ) : (
                            rows.map((row) => (
                                <tr key={row.enrollment_id}>
                                    <td className="px-4 py-3 align-top">
                                        <div className="font-medium text-mld-text-primary">
                                            {row.patient_first_name} {row.patient_last_name}
                                        </div>
                                        <div className="text-xs text-mld-text-secondary">user #{row.user_id}</div>
                                    </td>
                                    <td className="px-4 py-3 align-top">
                                        {row.organization_name ?? <span className="text-gray-400">—</span>}
                                    </td>
                                    <td className="px-4 py-3 align-top">{row.recommendation_name}</td>
                                    <td className="px-4 py-3 align-top">
                                        <div>{methodLabel(row.fulfillment_method)}</div>
                                        {row.kit_sku && (
                                            <div className="text-xs font-mono text-mld-text-secondary">
                                                {row.kit_sku}
                                            </div>
                                        )}
                                    </td>
                                    <td className="px-4 py-3 align-top text-xs text-mld-text-secondary">
                                        {row.fulfillment_method === 'home_kit' ? (
                                            <>
                                                <div>{row.address_1 || '—'}</div>
                                                {row.address_2 && <div>{row.address_2}</div>}
                                                <div>
                                                    {[row.city, row.state, row.zip].filter(Boolean).join(', ')}
                                                </div>
                                            </>
                                        ) : (
                                            <span className="text-gray-400">no shipping</span>
                                        )}
                                    </td>
                                    <td className="px-4 py-3 align-top text-xs">
                                        <div>Patient: {dollars(row.patient_amount_cents)}</div>
                                        <div className="text-mld-text-secondary">
                                            Employer: {dollars(row.employer_amount_cents)}
                                        </div>
                                    </td>
                                    <td className="px-4 py-3 align-top text-xs text-mld-text-secondary">
                                        {new Date(row.enrolled_at).toLocaleDateString()}
                                    </td>
                                    <td className="px-4 py-3 align-top text-right">
                                        <button
                                            type="button"
                                            onClick={() => onMark(row)}
                                            disabled={acting === row.enrollment_id}
                                            className="inline-flex items-center gap-1 rounded-md bg-mld-primary/10 px-3 py-1.5 text-xs font-semibold text-mld-primary hover:bg-mld-primary hover:text-white disabled:opacity-50">
                                            {acting === row.enrollment_id && (
                                                <Loader2 className="h-3 w-3 animate-spin" />
                                            )}
                                            {row.fulfillment_method === 'add_to_existing_draw'
                                                ? 'Mark Add-on Complete'
                                                : 'Mark Shipped'}
                                        </button>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default AdminBioMatchFulfillmentView;
